/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import { NsContent, NsSkeleton } from '@britishgasenergy/react-nucleus';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { NsTitle } from 'components/common/ns-title/NsTitle';
import { NsAnchor } from 'components/common/ns-anchor/NsAnchor';
import { Error } from 'components/common/error/Error';
import { getTariffDetailsThunk } from 'state/tariff-information/tariff-information-thunks';
import {
  selectIsLoadingTariffDetails,
  selectTariffDetailsErrors
} from 'state/tariff-information/tariff-information-slice';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { selectPostcode } from 'state/postcode/postcode-slice';
import { usePaymentMethod } from 'hooks/usePaymentMethod';
import { useTariffDetails } from 'components/direct-sales/tariff-information/hooks/useTariffDetails';
import { getFuelValue } from 'components/direct-sales/fuel-type/getFuelValue';
import { EvTariffRateCard } from './EvTariffRateCard';
import { NsCaveatLink } from 'components/common/ns-caveat-link/NsCaveatLink';

export const EvTariffRateInfo = () => {
  const dispatch = useDispatch();
  const { configEvTariffId } = useFlags();
  const { paymentMethod } = usePaymentMethod();
  const fuelType = useSelector(selectFuelType, shallowEqual);
  const isLoadingTariffDetails = useSelector(
    selectIsLoadingTariffDetails,
    shallowEqual
  );

  const errors = useSelector(selectTariffDetailsErrors, shallowEqual) || [];
  const tariffDetails = useTariffDetails() || {};
  const postcode = useSelector(selectPostcode, shallowEqual);
  const { common } = tariffDetails;
  const fuel = getFuelValue(fuelType);

  useEffect(() => {
    const callApi = async () => {
      try {
        await dispatch(
          getTariffDetailsThunk({
            tariffId: configEvTariffId,
            paymentMethod,
            economy7: false,
            isTOU: true,
            fuel,
            postcode
          })
        );
      } catch (e) {
        e && console.error(e);
      }
    };

    callApi();
  }, []);

  return (
    <NsContent>
      <NsTitle title="Electric Driver tariff" />
      <p>
        Charge smarter, pay less with our EV two-rate tariff. Get super-cheap
        electricity at night between 12am-5am.
        <NsCaveatLink>
          <>
            Eligibility criteria apply - find out more about our{' '}
            <NsAnchor
              href="https://www.britishgas.co.uk/energy/gas-and-electricity/ev-tariff.html"
              target="_blank"
            >
              electric vehicle energy tariffs.
            </NsAnchor>
          </>
        </NsCaveatLink>
      </p>
      <p>Here are the rates for our current EV tariff.</p>
      {isLoadingTariffDetails && (
        <NsContent>
          <p>
            <NsSkeleton></NsSkeleton>
          </p>
          <NsSkeleton></NsSkeleton>
          <NsSkeleton></NsSkeleton>
        </NsContent>
      )}
      {common && !isLoadingTariffDetails && (
        <EvTariffRateCard tariffDetails={tariffDetails} />
      )}
      <Error errors={errors} />
      <p>
        We regularly update our tariffs, so these rates aren&apos;t guaranteed.
        If we have a new EV tariff by the time you sign up, the rates would be
        different.
      </p>
    </NsContent>
  );
};
